import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus, FiDownload, FiTrash2, FiEye, FiUsers, FiUpload, FiEdit2 } from 'react-icons/fi';

const TABS = ['Documents', 'Document Types', 'Signatures'];
const OWNER_TYPES = ['organisation', 'employee', 'candidate', 'customer', 'supplier'];

const Documents = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('Documents');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);

  const [uploadForm, setUploadForm] = useState({ ownerType: 'organisation', ownerId: '', title: '', documentTypeCode: '', isConfidential: 'false', expiryDate: '', file: null });
  const [typeForm, setTypeForm] = useState({ code: '', name: '', requiresExpiry: false });
  const [signatureForm, setSignatureForm] = useState({ documentId: '', signerName: '', signerEmail: '' });

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [versions, setVersions] = useState(null);
  const [accessGrants, setAccessGrants] = useState(null);
  const [signatures, setSignatures] = useState(null);
  const [grantForm, setGrantForm] = useState({ userId: '', accessLevel: 'view' });
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const [processingJob, setProcessingJob] = useState(null);
  const [extractedFields, setExtractedFields] = useState(null);
  const [mockFieldsInput, setMockFieldsInput] = useState('invoiceNumber: INV-001\ntotal: 150.00\ndate: 2026-09-14');

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/documents'),
      apiClient.get('/documents/types'),
      apiClient.get('/organisations/current/users').catch(() => ({ data: { data: [] } })),
    ]).then(([docRes, typeRes, usersRes]) => {
      setDocuments(docRes.data.data);
      setDocumentTypes(typeRes.data.data);
      setOrgUsers(usersRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load documents')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const typeName = (id) => documentTypes.find((t) => t.id === id)?.name || '—';

  // ---- Documents ----
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) { showError('Choose a file first'); return; }
    try {
      const form = new FormData();
      form.append('file', uploadForm.file);
      form.append('ownerType', uploadForm.ownerType);
      if (uploadForm.ownerId) form.append('ownerId', uploadForm.ownerId);
      if (uploadForm.title) form.append('title', uploadForm.title);
      if (uploadForm.documentTypeCode) form.append('documentTypeCode', uploadForm.documentTypeCode);
      form.append('isConfidential', uploadForm.isConfidential);
      if (uploadForm.expiryDate) form.append('expiryDate', uploadForm.expiryDate);
      await apiClient.post('/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      success('✅ Document uploaded');
      setShowForm(false);
      setUploadForm({ ownerType: 'organisation', ownerId: '', title: '', documentTypeCode: '', isConfidential: 'false', expiryDate: '', file: null });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not upload document')); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await apiClient.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const fileName = match ? match[1] : (doc.title || 'document');
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { showError(getErrorMessage(err, 'Could not download document')); }
  };

  const handleArchive = async (documentId) => {
    if (!window.confirm('Archive this document? It will no longer show in the active list.')) return;
    try {
      await apiClient.delete(`/documents/${documentId}`);
      success('✅ Document archived');
      if (selectedDoc?.id === documentId) setSelectedDoc(null);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not archive document')); }
  };

  const viewDocument = async (doc) => {
    setSelectedDoc(doc);
    setVersions(null);
    setAccessGrants(null);
    setEditingTitle(false);
    setProcessingJob(null);
    setExtractedFields(null);
    try {
      const [versionsRes, accessRes] = await Promise.all([
        apiClient.get(`/documents/${doc.id}/versions`),
        apiClient.get(`/documents/${doc.id}/access`),
      ]);
      setVersions(versionsRes.data.data);
      setAccessGrants(accessRes.data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load document detail')); }
  };

  const handleAddVersion = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedDoc) return;
    try {
      const form = new FormData();
      form.append('file', file);
      await apiClient.post(`/documents/${selectedDoc.id}/versions`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      success('✅ New version uploaded');
      const { data } = await apiClient.get(`/documents/${selectedDoc.id}/versions`);
      setVersions(data.data);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not upload new version')); }
  };

  const startEditTitle = () => {
    setTitleDraft(selectedDoc.title || '');
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    try {
      await apiClient.patch(`/documents/${selectedDoc.id}`, { title: titleDraft });
      success('✅ Title updated');
      setEditingTitle(false);
      setSelectedDoc((prev) => ({ ...prev, title: titleDraft }));
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not update title')); }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/documents/${selectedDoc.id}/access`, grantForm);
      success('✅ Access granted');
      setGrantForm({ userId: '', accessLevel: 'view' });
      const { data } = await apiClient.get(`/documents/${selectedDoc.id}/access`);
      setAccessGrants(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not grant access')); }
  };

  const handleRevokeAccess = async (grantId) => {
    try {
      await apiClient.delete(`/documents/${selectedDoc.id}/access/${grantId}`);
      success('✅ Access revoked');
      const { data } = await apiClient.get(`/documents/${selectedDoc.id}/access`);
      setAccessGrants(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not revoke access')); }
  };

  // ---- Document Types ----
  const handleAddType = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/documents/types', typeForm);
      success('✅ Document type added');
      setShowForm(false);
      setTypeForm({ code: '', name: '', requiresExpiry: false });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add document type')); }
  };

  // ---- Signatures ----
  const loadSignatures = async (documentId) => {
    try {
      const { data } = await apiClient.get(`/documents/signatures/documents/${documentId}`);
      setSignatures(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load signatures')); }
  };

  const handleRequestSignature = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/documents/signatures', signatureForm);
      success('✅ Signature requested');
      setSignatureForm({ documentId: '', signerName: '', signerEmail: '' });
      if (signatureForm.documentId) loadSignatures(signatureForm.documentId);
    } catch (err) { showError(getErrorMessage(err, 'Could not request signature')); }
  };

  const handleSimulateSign = async (signatureId) => {
    try {
      await apiClient.post(`/documents/signatures/${signatureId}/simulate-sign`);
      success('✅ Signature simulated');
      if (signatureForm.documentId) loadSignatures(signatureForm.documentId);
    } catch (err) { showError(getErrorMessage(err, 'Could not simulate signature')); }
  };

  // ---- Document Processing (simulated OCR) ----
  const startProcessing = async (doc) => {
    try {
      const versionsRes = await apiClient.get(`/documents/${doc.id}/versions`);
      const latestVersion = versionsRes.data.data[0];
      const { data } = await apiClient.post('/documents/processing/jobs', { documentId: doc.id, documentVersionId: latestVersion.id });
      setProcessingJob(data.data);
      setExtractedFields(null);
      success('✅ Processing job created');
    } catch (err) { showError(getErrorMessage(err, 'Could not start processing')); }
  };

  const handleSimulateCompletion = async () => {
    try {
      const fields = mockFieldsInput.split('\n').filter(Boolean).map((line) => {
        const [fieldName, ...rest] = line.split(':');
        return { fieldName: fieldName.trim(), extractedValue: rest.join(':').trim() };
      });
      await apiClient.post(`/documents/processing/jobs/${processingJob.id}/simulate-completion`, { fields });
      success('✅ Extraction simulated');
      const { data } = await apiClient.get(`/documents/processing/jobs/${processingJob.id}/fields`);
      setExtractedFields(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not simulate completion')); }
  };

  const handleCorrectField = async (fieldId, correctedValue) => {
    try {
      await apiClient.patch(`/documents/processing/fields/${fieldId}/correct`, { correctedValue });
      success('✅ Field corrected');
      const { data } = await apiClient.get(`/documents/processing/jobs/${processingJob.id}/fields`);
      setExtractedFields(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not correct field')); }
  };

  const handleValidateField = async (fieldId) => {
    try {
      await apiClient.patch(`/documents/processing/fields/${fieldId}/validate`);
      const { data } = await apiClient.get(`/documents/processing/jobs/${processingJob.id}/fields`);
      setExtractedFields(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not validate field')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Documents</h1>
        <p>Upload, version, share, and track sign-off on files</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); setSelectedDoc(null); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Documents' && (
        <>
          <section className="page-section">
            <div className="section-header">
              <h2>Documents</h2>
              <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
                <FiUpload size={14} /> {showForm ? 'Cancel' : 'Upload'}
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleUpload} className="form-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="form-field full-width">
                  <label>File *</label>
                  <input required type="file" onChange={(e) => setUploadForm(p => ({ ...p, file: e.target.files[0] }))} />
                </div>
                <div className="form-field"><label>Title</label><input value={uploadForm.title} onChange={(e) => setUploadForm(p => ({ ...p, title: e.target.value }))} placeholder="Defaults to file name" /></div>
                <div className="form-field">
                  <label>Owner Type *</label>
                  <select required value={uploadForm.ownerType} onChange={(e) => setUploadForm(p => ({ ...p, ownerType: e.target.value }))}>
                    {OWNER_TYPES.map((t) => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label>Document Type</label>
                  <select value={uploadForm.documentTypeCode} onChange={(e) => setUploadForm(p => ({ ...p, documentTypeCode: e.target.value }))}>
                    <option value="">-- none --</option>
                    {documentTypes.map((t) => <option key={t.id} value={t.code}>{t.name}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Expiry Date</label><input type="date" value={uploadForm.expiryDate} onChange={(e) => setUploadForm(p => ({ ...p, expiryDate: e.target.value }))} /></div>
                <div className="form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                    <input type="checkbox" checked={uploadForm.isConfidential === 'true'} onChange={(e) => setUploadForm(p => ({ ...p, isConfidential: e.target.checked ? 'true' : 'false' }))} style={{ width: 'auto' }} />
                    Confidential (visible only to uploader, managers, and explicit access grants)
                  </label>
                </div>
                <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Upload</button></div>
              </form>
            )}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Title</th><th>Owner</th><th>Type</th><th>Confidential</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                  ) : documents.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No documents yet</td></tr>
                  ) : documents.map((d) => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 600 }}>{d.title || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{d.ownerType}</td>
                      <td>{d.documentTypeId ? typeName(d.documentTypeId) : '—'}</td>
                      <td>{d.isConfidential ? 'Yes' : 'No'}</td>
                      <td><span className="badge" style={{ background: d.status === 'archived' ? '#9ca3af' : '#10b981', color: 'white' }}>{d.status}</span></td>
                      <td>
                        <div className="actions">
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewDocument(d)}>
                            <FiEye size={14} /> View
                          </button>
                          <button className="action-icon edit" title="Download" onClick={() => handleDownload(d)}>
                            <FiDownload size={16} />
                          </button>
                          {d.status !== 'archived' && (
                            <button className="action-icon delete" title="Archive" onClick={() => handleArchive(d.id)}>
                              <FiTrash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {selectedDoc && (
            <section className="page-section">
              <div className="section-header">
                <h2>
                  {editingTitle ? (
                    <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} style={{ padding: '0.3rem' }} />
                      <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={handleSaveTitle}>Save</button>
                    </span>
                  ) : (
                    <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {selectedDoc.title || 'Untitled'}
                      <button className="action-icon edit" title="Rename" onClick={startEditTitle}><FiEdit2 size={14} /></button>
                    </span>
                  )}
                </h2>
                <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => setSelectedDoc(null)}>Close</button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Versions</label>
                {versions === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : (
                  <div className="table-container" style={{ marginBottom: '0.75rem' }}>
                    <table className="data-table">
                      <thead><tr><th>Version</th><th>Uploaded</th><th>File</th></tr></thead>
                      <tbody>
                        {versions.map((v) => (
                          <tr key={v.id}>
                            <td>v{v.versionNumber}</td>
                            <td>{new Date(v.createdAt).toLocaleString()}</td>
                            <td>{v.fileName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <label className="btn btn-sm btn-outline" style={{ width: 'auto', display: 'inline-flex', cursor: 'pointer' }}>
                  <FiUpload size={14} /> Upload New Version
                  <input type="file" onChange={handleAddVersion} style={{ display: 'none' }} />
                </label>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                  <FiUsers size={14} style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} /> Access Grants
                </label>
                <form onSubmit={handleGrantAccess} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <select required value={grantForm.userId} onChange={(e) => setGrantForm(p => ({ ...p, userId: e.target.value }))} style={{ padding: '0.4rem' }}>
                    <option value="">-- select user --</option>
                    {orgUsers.map((u) => <option key={u.userId} value={u.userId}>{u.firstName} {u.lastName}</option>)}
                  </select>
                  <select value={grantForm.accessLevel} onChange={(e) => setGrantForm(p => ({ ...p, accessLevel: e.target.value }))} style={{ padding: '0.4rem' }}>
                    <option value="view">View</option>
                    <option value="edit">Edit</option>
                    <option value="full">Full</option>
                  </select>
                  <button type="submit" className="btn btn-sm btn-primary" style={{ width: 'auto' }}>Grant</button>
                </form>
                {accessGrants === null ? <p style={{ color: '#6b7280' }}>Loading...</p> : accessGrants.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>No explicit grants — visible per standard document permissions only.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {accessGrants.map((g) => (
                      <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '0.85rem' }}>
                        <span>{orgUsers.find(u => u.userId === g.userId)?.firstName || g.userId} — {g.accessLevel}</span>
                        <button className="action-icon delete" title="Revoke" onClick={() => handleRevokeAccess(g.id)}><FiTrash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Data Extraction (simulated)</label>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                  ⚠️ No real OCR provider is connected in this environment — "Simulate Completion" lets you supply the extracted values directly to exercise the review/correction flow.
                </p>
                {!processingJob ? (
                  <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => startProcessing(selectedDoc)}>Start Processing Job</button>
                ) : (
                  <>
                    <p style={{ fontSize: '0.85rem' }}>Job status: <strong>{processingJob.status}</strong></p>
                    {!extractedFields && (
                      <div style={{ marginBottom: '1rem' }}>
                        <textarea rows={4} value={mockFieldsInput} onChange={(e) => setMockFieldsInput(e.target.value)} style={{ width: '100%', padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem' }} placeholder="fieldName: value (one per line)" />
                        <button className="btn btn-sm btn-primary" style={{ width: 'auto', marginTop: '0.5rem' }} onClick={handleSimulateCompletion}>Simulate Completion</button>
                      </div>
                    )}
                    {extractedFields && (
                      <div className="table-container">
                        <table className="data-table">
                          <thead><tr><th>Field</th><th>Extracted</th><th>Corrected</th><th>Status</th><th>Actions</th></tr></thead>
                          <tbody>
                            {extractedFields.map((f) => (
                              <tr key={f.id}>
                                <td>{f.fieldName}</td>
                                <td>{f.extractedValue}</td>
                                <td>
                                  <input
                                    defaultValue={f.correctedValue || ''}
                                    onBlur={(e) => e.target.value && e.target.value !== f.correctedValue && handleCorrectField(f.id, e.target.value)}
                                    placeholder="Type to correct"
                                    style={{ padding: '0.3rem', width: '120px' }}
                                  />
                                </td>
                                <td><span className="badge" style={{ background: f.validationStatus === 'validated' ? '#10b981' : '#f59e0b', color: 'white' }}>{f.validationStatus}</span></td>
                                <td>
                                  {f.validationStatus !== 'validated' && (
                                    <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleValidateField(f.id)}>Validate</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {tab === 'Document Types' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Document Types</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Type'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddType} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Code *</label><input required value={typeForm.code} onChange={(e) => setTypeForm(p => ({ ...p, code: e.target.value }))} placeholder="passport" /></div>
              <div className="form-field"><label>Name *</label><input required value={typeForm.name} onChange={(e) => setTypeForm(p => ({ ...p, name: e.target.value }))} placeholder="Passport" /></div>
              <div className="form-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                  <input type="checkbox" checked={typeForm.requiresExpiry} onChange={(e) => setTypeForm(p => ({ ...p, requiresExpiry: e.target.checked }))} style={{ width: 'auto' }} />
                  Requires expiry date
                </label>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Name</th><th>Requires Expiry</th></tr></thead>
              <tbody>
                {documentTypes.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No document types yet</td></tr>
                ) : documentTypes.map((t) => (
                  <tr key={t.id}>
                    <td style={{ fontFamily: 'monospace' }}>{t.code}</td>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td>{t.requiresExpiry ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Signatures' && (
        <section className="page-section">
          <div className="section-header"><h2>Signature Requests</h2></div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            This is a simulated sign-off flow for internal tracking, not a legally binding e-signature service like DocuSign — there's no real signer authentication or cryptographic signing behind it.
          </p>
          <form onSubmit={handleRequestSignature} className="form-grid" style={{ marginBottom: '1.5rem' }}>
            <div className="form-field">
              <label>Document *</label>
              <select required value={signatureForm.documentId} onChange={(e) => { setSignatureForm(p => ({ ...p, documentId: e.target.value })); loadSignatures(e.target.value); }}>
                <option value="">-- select --</option>
                {documents.map((d) => <option key={d.id} value={d.id}>{d.title || d.id}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Signer Name *</label><input required value={signatureForm.signerName} onChange={(e) => setSignatureForm(p => ({ ...p, signerName: e.target.value }))} /></div>
            <div className="form-field"><label>Signer Email</label><input type="email" value={signatureForm.signerEmail} onChange={(e) => setSignatureForm(p => ({ ...p, signerEmail: e.target.value }))} /></div>
            <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Request Signature</button></div>
          </form>
          {signatures !== null && (
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Signer</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {signatures.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No signature requests for this document yet</td></tr>
                  ) : signatures.map((s) => (
                    <tr key={s.id}>
                      <td>{s.signerName}</td>
                      <td><span className="badge" style={{ background: s.status === 'signed' ? '#10b981' : '#f59e0b', color: 'white' }}>{s.status}</span></td>
                      <td>
                        {s.status !== 'signed' && (
                          <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => handleSimulateSign(s.id)}>
                            Simulate Sign
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </motion.div>
  );
};

export default Documents;